import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Pill } from "@/components/ui/Pill";
import { Skeleton } from "@/components/ui/Skeleton";
import { useApiQuery } from "@/hooks/useApi";
import { qk } from "@/lib/queryKeys";
import { S, T } from "@/lib/strings";
import { formatBytes, formatDuration } from "@/lib/utils";
import type { FleetSettings as FleetSettingsModel } from "@/types/fleet";

/**
 * Read-only in phase 1.
 *
 * `PUT /api/v1/settings/fleet` exists, but a write here pushes a new
 * `config_version` to every enrolled device at once (fleet-v1.md §6) — that is
 * a fleet-wide mutation with no preview and no undo, and it deserves the
 * `ConfirmImpactDialog` treatment rather than a form that saves on blur. Phase 2.
 */
export function FleetSettings() {
  const query = useApiQuery<FleetSettingsModel>(qk.fleetSettings, "/settings/fleet");

  return (
    <>
      <PageHeader
        title={S.fleetSettingsTitle}
        hint={S.fleetSettingsHint}
        actions={
          <Pill variant="muted" dot={false}>
            {S.fleetSettingsReadOnly}
          </Pill>
        }
      />

      {query.isError && (
        <QueryErrorCard error={query.error} onRetry={() => void query.refetch()} className="mb-3" />
      )}

      {query.isPending ? (
        <div className="grid max-w-4xl gap-3 lg:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      ) : query.data ? (
        <div className="max-w-4xl space-y-3">
          <div className="rounded-lg border border-[rgba(245,158,11,0.2)] bg-[rgba(245,158,11,0.04)] px-3 py-2 text-[11px] text-[#fcd34d]">
            {S.fleetSettingsReadOnlyHint}
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{S.sectionLimits}</CardTitle>
              </CardHeader>
              <CardContent>
                <Rows
                  rows={[
                    [S.fieldMaxConcurrentRuns, String(query.data.max_concurrent_runs)],
                    [
                      S.fieldDefaultStagger,
                      `${query.data.default_stagger_ms} ms · ${formatDuration(
                        query.data.default_stagger_ms,
                      )}`,
                    ],
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{S.sectionRetention}</CardTitle>
              </CardHeader>
              <CardContent>
                <Rows
                  rows={[
                    [S.fieldKeepScreenshots, `${query.data.keep_screenshots_days} 日`],
                    [S.fieldRetentionDays, `${query.data.retention_days} 日`],
                    [
                      S.fieldDiskUsage,
                      T.diskUsage(
                        formatBytes(query.data.disk_usage_bytes),
                        formatBytes(query.data.disk_total_bytes),
                      ),
                    ],
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{S.sectionSecrets}</CardTitle>
              </CardHeader>
              <CardContent>
                <Rows
                  rows={[
                    [
                      S.fieldAgensKey,
                      query.data.agens_key_fingerprint
                        ? `sha256:${query.data.agens_key_fingerprint}`
                        : S.keyNotSet,
                    ],
                  ]}
                />
                <p className="mt-1.5 text-[11px] text-[var(--color-fg-faint)]">
                  {S.keyFingerprintOnly}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{S.settingsFleetManaged}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-2 text-[11px] text-[var(--color-fg-faint)]">
                  {S.settingsFleetManagedHint}
                </p>
                <Rows
                  rows={Object.entries(query.data.settings ?? {}).map(([k, v]) => [
                    k,
                    v === null || v === "" ? "—" : String(v),
                  ])}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Rows({ rows }: { rows: Array<[string, string]> }) {
  if (rows.length === 0) {
    return <p className="text-[11px] text-[var(--color-fg-faint)]">{S.empty}</p>;
  }
  return (
    <dl className="space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-3 text-[12px]">
          <dt className="w-48 shrink-0 truncate font-mono text-[var(--color-fg-subtle)]">{k}</dt>
          <dd className="min-w-0 break-all font-mono text-[var(--color-fg-muted)]">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
